/**
 * Normalises the `attachments` jsonb carried by both products and itinerary
 * items into display rows for the Attachments preview tab.
 *
 * The stored shape is `ProductAttachment` (packages/shared/src/schemas/products.ts)
 * and is loose by design — every field is optional, and rows written by
 * different surfaces populate different ones. Two of those inconsistencies are
 * load-bearing here:
 *
 * - **The filename lives in one of two places.** Ticket/legacy rows carry a
 *   `file` object with `name`; rows written by the generic files tab have no
 *   `file` at all and carry `fileNameBase` instead. Reading only one of them
 *   renders raw UploadThing keys to the user.
 * - **`showExternally` is the Internal/Public flag.** Absent means Internal:
 *   an attachment is customer-visible only when something explicitly said so.
 */

import { uploadthingFileUrl } from '@travel-planner/uploadthing/client';

export type AttachmentRow = {
  // Stable per-render key. Falls back through id → fileKey → url → index, none
  // of which is guaranteed present on a loose jsonb row.
  key: string;
  fileName: string;
  // Uppercased extension for the Type column (PDF, DOCX, JPG…). Empty when the
  // filename carries no usable extension.
  fileType: string;
  isPublic: boolean;
  // Absolute URL, or null when the row carries neither a url nor a file key —
  // such a row is listed but cannot be opened.
  url: string | null;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const asString = (value: unknown): string =>
  typeof value === 'string' ? value : '';

const resolveFileName = (row: Record<string, unknown>): string => {
  const file = isRecord(row.file) ? row.file : null;
  const fromFile = file ? asString(file.name) : '';
  if (fromFile) return fromFile;

  const base = asString(row.fileNameBase);
  if (base) return base;

  // Last resort so the row is still identifiable; never the raw storage key,
  // which is meaningless to a consultant.
  return asString(row.caption) || 'Untitled file';
};

const resolveFileType = (fileName: string, row: Record<string, unknown>) => {
  const dot = fileName.lastIndexOf('.');
  if (dot > 0 && dot < fileName.length - 1) {
    return fileName.slice(dot + 1).toUpperCase();
  }
  // No extension — fall back to the declared type (IMAGE/VIDEO/…), which is
  // coarser but better than a blank column.
  return asString(row.type).toUpperCase();
};

const resolveUrl = (row: Record<string, unknown>): string | null => {
  const url = asString(row.url);
  if (url) return url;
  const fileKey = asString(row.fileKey);
  return fileKey ? uploadthingFileUrl(fileKey) : null;
};

/**
 * `includeImages: false` (the default) drops rows the gallery already renders,
 * so the Attachments tab lists documents rather than repeating the photos.
 */
export const toAttachmentRows = (
  attachments: unknown,
  options: { includeImages?: boolean } = {}
): AttachmentRow[] => {
  if (!Array.isArray(attachments)) return [];

  return attachments.flatMap((raw, index) => {
    if (!isRecord(raw)) return [];

    const declaredType = asString(raw.type).toUpperCase();
    if (!options.includeImages && declaredType === 'IMAGE') return [];

    const fileName = resolveFileName(raw);
    return [
      {
        key:
          asString(raw.id) ||
          asString(raw.fileKey) ||
          asString(raw.url) ||
          `attachment-${index}`,
        fileName,
        fileType: resolveFileType(fileName, raw),
        isPublic: raw.showExternally === true,
        url: resolveUrl(raw),
      },
    ];
  });
};
